// src/components/modules/TAI/TAITrasladoCard.js

import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, Typography, Space, Row, Col, Badge, Tag, Checkbox } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  CarOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CarFilled,
  InfoOutlined,
  PhoneOutlined,
  CommentOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { format, addHours } from "date-fns";
import StatusTag from "../../common/StatusTag";
import ColoredCard from "../../common/ColoredCard";
import { formatCurrencyCOP } from "../../../utils/formatCurrency";
import ObservacionesDialog from "../Dialog/ObservacionesDialog";

const { Text } = Typography;

// Función para ajustar la fecha según la zona horaria
const ajustarFecha = (fecha) => {
  return addHours(new Date(fecha), 5);
};

// Colores por estado de asignación TAI
const getEstadoAsignacionColor = (estado) => {
  const colors = {
    pendiente: "#faad14",
    asignado: "#1890ff",
    confirmado: "#52c41a",
    ejecutado: "#13c2c2",
    cancelado: "#ff4d4f",
  };
  return colors[estado] || "#8c8c8c";
};

// Colores por tipo de trayecto TAI
const getTrayectoColor = (trayecto) => {
  const colors = {
    interveredal: "#722ed1",
    intermunicipal: "#2f54eb",
    urbano: "#13c2c2",
  };
  return colors[trayecto] || "#8c8c8c";
};

// Funciones para obtener colores según el estado
const getStatusColor = (status) => {
  const statusColors = {
    Cancelado: "#b10e1a",
    Finalizado: "#4d4d4d",
    Fallido: "#4a2c04",
    FallidoXvalidar: "#d4380d",
    Asignado: "#1f7362",
    "Pre-Asignado": "#0c27a0",
    Programado: "#6c06b5",
    Enterado: "#0c5b07",
    Iniciado: "#fa7114",
    Aceptado: "#088A08",
    "En Camino": "#FFBF00",
    Esperando: "#a44706",
    default: "#b0b500",
  };
  return statusColors[status] || statusColors.default;
};

const getConfirmacionColor = (confirmacion) => {
  return confirmacion === "Confirmado" ? "#52c41a" : "#f5222d";
};

const getSeguimientoColor = (seguimiento) => {
  return seguimiento === "Validado" ? "#52c41a" : "#f5222d";
};

const TAITrasladoCard = ({
  traslado,
  isSelected,
  onSelect,
  onRefresh,
  hasPermission,
  onContextMenu,
}) => {
  const statusColor = getStatusColor(traslado.status);
  const confirmacionColor = getConfirmacionColor(traslado.confirmacion);
  const estadoAsignacionColor = getEstadoAsignacionColor(traslado.tai_estado_asignacion);
  const [observacionesOpen, setObservacionesOpen] = useState(false);

  // Solo permitir selección si está pendiente
  const canSelect =
    hasPermission("ASIGNAR_TAI") &&
    traslado.tai_estado_asignacion === "pendiente";

  const handleContextMenu = (event) => {
    event.preventDefault();
    if (onContextMenu) {
      onContextMenu(event, traslado);
    }
  };

  const handleOpenObservaciones = (e) => {
    e.stopPropagation();
    setObservacionesOpen(true);
  };

  const handleCloseObservaciones = () => {
    setObservacionesOpen(false);
  };

  return (
    <ColoredCard color={estadoAsignacionColor}>
      <Card
        className="tai-traslado-card"
        key={traslado.id}
        onContextMenu={handleContextMenu}
        hoverable
        variant="borderless"
        style={{ padding: 0 }}
      >
        {/* Header con CS-ID y estados */}
        <div
          style={{
            backgroundColor: "#0a2e5c",
            padding: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            flexWrap: "wrap",
          }}
        >
          <Space>
            {canSelect && (
              <Checkbox
                checked={isSelected}
                onChange={(e) => onSelect(e.target.checked)}
                style={{ marginRight: 8 }}
              />
            )}
            <Text
              strong
              style={{
                color: "#fff",
                marginRight: "8px",
                wordBreak: "break-all",
              }}
            >
              CS-{traslado.id}
            </Text>
          </Space>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {/* Tag TAI con tipo de trayecto */}
            {traslado.tai_trayecto_tipo && (
              <Tag
                color={getTrayectoColor(traslado.tai_trayecto_tipo)}
                icon={<CarOutlined />}
                style={{ margin: 0 }}
              >
                TAI {traslado.tai_trayecto_tipo.charAt(0).toUpperCase() + traslado.tai_trayecto_tipo.slice(1)}
              </Tag>
            )}

            {/* Tag de Estado del Traslado */}
            <StatusTag color={statusColor} text={traslado.status} />

            {/* Tag de Estado de Asignación TAI */}
            <StatusTag
              color={estadoAsignacionColor}
              text={traslado.tai_estado_asignacion?.toUpperCase() || "PENDIENTE"}
            />

            {/* Tag de Confirmación */}
            <StatusTag
              color={confirmacionColor}
              text={traslado.confirmacion === "Confirmado" ? "Confirmado" : "Sin Confirmar"}
              icon={traslado.confirmacion === "Confirmado" ? <CheckCircleOutlined /> : null}
            />

            {/* Tag de Validación */}
            <StatusTag
              color={getSeguimientoColor(traslado.seguimiento)}
              text={traslado.seguimiento === "Validado" ? "Validado" : "Sin Validar"}
              icon={traslado.seguimiento === "Validado" ? <CheckCircleFilled /> : null}
            />
          </div>
        </div>

        {/* Distribución de 3 columnas */}
        <Row gutter={[16, 16]} style={{ padding: "10px" }}>
          {/* Columna izquierda: Fechas y horas */}
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size="small">
              <Space>
                <ClockCircleOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>{format(ajustarFecha(traslado.fecha), "dd/MM/yyyy")}</Text>
              </Space>
              <Space>
                <ClockCircleOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>Hora Recogida: {traslado.hora_recogida?.slice(0, 5)}</Text>
              </Space>
              {traslado.hora_llegada && (
                <Space>
                  <ClockCircleOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                  <Text>Hora Llegada: {traslado.hora_llegada?.slice(0, 5)}</Text>
                </Space>
              )}
            </Space>
          </Col>

          {/* Columna central: Origen y destino */}
          <Col xs={24} sm={12} md={8} style={{ textAlign: "center" }}>
            <Space direction="vertical" size="small">
              <Space>
                <EnvironmentOutlined
                  style={{
                    fontSize: "18px",
                    color: traslado.origen_lugar ? "#52c41a" : "#cccccc",
                    filter: traslado.origen_lugar ? "drop-shadow(0 0 2px rgba(82, 196, 26, 0.4))" : "none",
                  }}
                />
                <Text>
                  <span style={{ fontWeight: "bold" }}>{traslado.origen_lugar || traslado.origen_ciudad}</span>{" "}
                  {traslado.origen_direccion && `${traslado.origen_direccion} -`} {traslado.origen_ciudad}
                </Text>
              </Space>
              <Space>
                <EnvironmentOutlined
                  style={{
                    fontSize: "18px",
                    color: traslado.destino_lugar ? "#f5222d" : "#cccccc",
                    filter: traslado.destino_lugar ? "drop-shadow(0 0 2px rgba(245, 34, 45, 0.4))" : "none",
                  }}
                />
                <Text>
                  <span style={{ fontWeight: "bold" }}>{traslado.destino_lugar || traslado.destino_ciudad}</span>{" "}
                  {traslado.destino_direccion && `${traslado.destino_direccion} -`} {traslado.destino_ciudad}
                </Text>
              </Space>
            </Space>
          </Col>

          {/* Columna derecha: Información del paciente */}
          <Col xs={24} sm={24} md={8} style={{ textAlign: "right" }}>
            <Space direction="vertical" size="small">
              <Space>
                <UserOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>{traslado.nombre_paciente}</Text>
                <Badge
                  count={traslado.observaciones_count || 0}
                  style={{
                    backgroundColor: (traslado.observaciones_count || 0) > 0 ? "#52c41a" : "#d9d9d9",
                    cursor: "pointer",
                  }}
                  title={`${traslado.observaciones_count || 0} observaciones`}
                >
                  <CommentOutlined
                    style={{
                      fontSize: "18px",
                      color: (traslado.observaciones_count || 0) > 0 ? "#1890ff" : "#8c8c8c",
                      cursor: "pointer",
                    }}
                    onClick={handleOpenObservaciones}
                  />
                </Badge>
              </Space>
              <Space>
                <Text style={{ color: "#8c8c8c" }}>{traslado.tipo_documento_paciente}</Text>
              </Space>
              <Space>
                <IdcardOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>{traslado.numero_documento_paciente}</Text>
              </Space>
              {traslado.numero_contacto_paciente && (
                <Space>
                  <PhoneOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                  <Text>{traslado.numero_contacto_paciente}</Text>
                </Space>
              )}
            </Space>
          </Col>
        </Row>

        {/* Cliente TAI / EPS y Copago */}
        <Row gutter={[16, 16]} style={{ padding: "0 10px", textAlign: "center" }}>
          <Col
            xs={24}
            sm={12}
            md={12}
            style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}
          >
            <MedicineBoxOutlined style={{ fontSize: "16px", color: "#722ed1", marginRight: 8 }} />
            <Text>
              <strong>Cliente TAI:</strong>{" "}
              {traslado.cliente_tai?.nombre_cliente || traslado.empresa}
            </Text>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={12}
            style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
          >
            <DollarOutlined style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }} />
            <Text>{formatCurrencyCOP(traslado.copago_diario) || "$ 0,00"}</Text>
          </Col>
        </Row>

        {/* Transportadora asignada */}
        {traslado.transportadora && (
          <Row gutter={[16, 16]} style={{ padding: "5px 10px", textAlign: "center" }}>
            <Col xs={24} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <TeamOutlined style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }} />
              <Text>
                <strong>Transportadora:</strong>{" "}
                <span style={{ color: "#1890ff" }}>{traslado.transportadora.nombre}</span>
              </Text>
            </Col>
          </Row>
        )}

        {/* Autorizaciones */}
        <Row gutter={[16, 16]} style={{ padding: "0 10px", textAlign: "center", marginTop: "10px" }}>
          {traslado.autorizacion && (
            <Col xs={24} sm={12} md={12}>
              <Space>
                <InfoCircleOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>
                  <strong>Autorización:</strong> {traslado.autorizacion}
                </Text>
              </Space>
            </Col>
          )}
          {traslado.tai_numero_tiquete && (
            <Col xs={24} sm={12} md={12}>
              <Space>
                <InfoCircleOutlined style={{ fontSize: "16px", color: "#722ed1" }} />
                <Text>
                  <strong>Tiquete TAI:</strong> {traslado.tai_numero_tiquete}
                </Text>
              </Space>
            </Col>
          )}
        </Row>

        {/* Trayecto y observaciones */}
        <Row gutter={[16, 16]} style={{ padding: "0 10px", textAlign: "center" }}>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <CarOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
              <Text>{traslado.trayecto}</Text>
            </Space>
          </Col>
          {traslado.observaciones_traslado && (
            <Col xs={24} sm={24} md={24}>
              <Space>
                <InfoCircleOutlined style={{ fontSize: "16px", color: "#faad14" }} />
                <Text style={{ color: "orange" }}>{traslado.observaciones_traslado}</Text>
              </Space>
            </Col>
          )}
          {traslado.indicaciones_conductor && (
            <Col xs={24} sm={24} md={24}>
              <Space>
                <InfoOutlined style={{ fontSize: "16px", color: "#faad14" }} />
                <Text style={{ color: "orange" }}>{traslado.indicaciones_conductor}</Text>
              </Space>
            </Col>
          )}
        </Row>

        {/* Footer: Tipo de traslado y vehículo */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: "10px",
            padding: "10px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Text style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
            {traslado.tipo_traslado || "TAI"} - {traslado.tipo_viaje || "Sencillo"}
          </Text>

          {traslado.nombre_vehiculo && traslado.placa && (
            <Text style={{ display: "flex", alignItems: "center" }}>
              <CarFilled style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }} />
              {traslado.nombre_vehiculo} - {traslado.placa}
            </Text>
          )}
        </div>

        {/* Modal de Observaciones */}
        <ObservacionesDialog
          open={observacionesOpen}
          onClose={handleCloseObservaciones}
          trasladoId={traslado.id}
          title={`Observaciones - CS-${traslado.id}`}
          key={`observaciones-${traslado.id}`}
        />
      </Card>
    </ColoredCard>
  );
};

TAITrasladoCard.propTypes = {
  traslado: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fecha: PropTypes.string.isRequired,
    hora_recogida: PropTypes.string.isRequired,
    hora_llegada: PropTypes.string,
    nombre_paciente: PropTypes.string.isRequired,
    tipo_documento_paciente: PropTypes.string,
    numero_documento_paciente: PropTypes.string,
    numero_contacto_paciente: PropTypes.string,
    origen_lugar: PropTypes.string,
    origen_ciudad: PropTypes.string,
    origen_direccion: PropTypes.string,
    destino_lugar: PropTypes.string,
    destino_ciudad: PropTypes.string,
    destino_direccion: PropTypes.string,
    empresa: PropTypes.string,
    autorizacion: PropTypes.string,
    trayecto: PropTypes.string,
    observaciones_traslado: PropTypes.string,
    indicaciones_conductor: PropTypes.string,
    tai_estado_asignacion: PropTypes.string,
    tai_trayecto_tipo: PropTypes.string,
    tai_numero_tiquete: PropTypes.string,
    status: PropTypes.string,
    confirmacion: PropTypes.string,
    seguimiento: PropTypes.string,
    copago_diario: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tipo_traslado: PropTypes.string,
    tipo_viaje: PropTypes.string,
    nombre_vehiculo: PropTypes.string,
    placa: PropTypes.string,
    observaciones_count: PropTypes.number,
    transportadora: PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
    }),
    cliente_tai: PropTypes.shape({
      id: PropTypes.number,
      nombre_cliente: PropTypes.string,
    }),
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func,
};

export default TAITrasladoCard;
